import React, { useState, useEffect } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MyAccountService from "../../services/MyAccountService";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import { validateUpdateInput, parseUpdateError } from "../../utils/updateUserErrorHandler";

import "./ProfileModals.css";

const EditUserModal = ({ isOpen, onClose, user, token, onUpdated }) => {
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email] = useState(user?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetState = () => {
    setUsername(user?.username || "");
    setName(user?.name || "");
    setSurname(user?.surname || "");
    setPhoneNumber(user?.phone_number || "");
    setOldPassword("");
    setNewPassword("");
    setError("");
    setSaving(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateUpdateInput({
      username,
      oldPassword,
      newPassword,
      name,
      surname,
      phoneNumber,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        email,
        username,
        name,
        surname,
        phone_number: phoneNumber,
        old_password: oldPassword || null,
        new_password: newPassword || null,
      };

      const updated = await MyAccountService.updateUserData(payload);
      setSuccess("Profile updated successfully.");
      onUpdated?.(updated);
    } catch (err) {
      const friendly = parseUpdateError(err);
      setError(friendly);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Edit profile"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        <label>
          Username
          <MainInput
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          Name
          <MainInput
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          Surname
          <MainInput
            type="text"
            value={surname}
            onChange={(e) => {
              setSurname(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          Phone number
          <MainInput
            type="text"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          Current password
          <MainInput
            type="password"
            value={oldPassword}
            placeholder="Leave empty to keep current password"
            onChange={(e) => {
              setOldPassword(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          New password
          <MainInput
            type="password"
            value={newPassword}
            placeholder="Leave empty to keep current password"
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError("");
            }}
          />
        </label>

        {error && (
          <div className="profile-user-edit-error">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-user-edit-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditUserModal;
